/**
 * Install the portal as an app (QM-D19-001 / QM-NC02-005). Android Chrome: beforeinstallprompt -> Install
 * button; iOS Safari (no prompt API): Share > Add to Home Screen instructions. Installed app starts at
 * /approvals (manifest start_url).
 */
import { useEffect, useState } from 'react';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';

interface InstallPromptEvent extends Event { prompt(): Promise<void> }

export function platformOf(ua: string): 'ios' | 'android' | 'other' {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

export function InstallApp({ userAgent }: { userAgent?: string }) {
  const { t } = useApp();
  const [evt, setEvt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPrompt = (e: Event) => { e.preventDefault(); setEvt(e as InstallPromptEvent); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    if (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) setInstalled(true);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);
  if (installed) return null;
  const platform = platformOf(ua);
  return (
    <Panel className="qm-install" aria-labelledby="install-title">
      <Heading level={2} id="install-title">{t('shell.install.title')}</Heading>
      <Text tone="muted">{t('shell.install.intro')}</Text>
      {platform === 'ios' ? <Text>{t('shell.install.ios')}</Text> : evt ? (
        <Button variant="primary" onClick={() => void evt.prompt().then(() => setEvt(null))}>{t('shell.install.button')}</Button>
      ) : <Text>{t('shell.install.browser')}</Text>}
    </Panel>
  );
}
